/**
 * Mobile History — Deals | Orders | Closed as scrollable HTML tables
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "assets/css/history-table.css";
import moment from "moment";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { api } from "utils/api";
import { getSessionData } from "utils/common";
import {
  formatApiDateYmd,
  formatUiDateTime,
  UI_DATEPICKER_FORMAT,
} from "utils/dateFormats";
import { deleteOrder } from "Store/Reducer/orderbook.slice";

const TABS = [
  { id: "deals", label: "Deals" },
  { id: "orders", label: "Orders" },
  { id: "closed", label: "Closed" },
];

const ORDER_CHIPS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "done", label: "Done" },
];

function endpointForTab(tab) {
  if (tab === "orders") return "orders/get-history";
  if (tab === "closed") return "positions/get-closed";
  return "deals/get";
}

function fmt(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function fmtTime(...vals) {
  for (const v of vals) {
    const t = formatUiDateTime(v, "");
    if (t) return t;
  }
  return "—";
}

function isHistoryOrderPending(row) {
  if (!row) return false;
  const st = Number(row.state ?? 0);
  const status = String(row.status || row.stateName || "").toLowerCase();
  if (status === "filled" || status === "cancelled" || status === "expired" || status === "rejected") {
    return false;
  }
  if (st === 2 || st === 3 || st === 4 || st === 5) return false;
  return st === 0 || st === 1 || status === "pending" || status === "placed" || status.includes("partial");
}

export default function History() {
  const dispatch = useDispatch();
  const session = getSessionData() || {};
  const userId = session.userId || session.id || session.UserId;
  const roles = session.roles || [session.roleName || session.role || "User"];
  const isUser = String(roles[0] || "").toLowerCase() === "user";
  const { userRole } = useSelector((s) => s.auth || {});
  const roleName = String(userRole || roles[0] || "").toLowerCase();
  const canCancelPendingOrders = !["partner", "sub broker"].includes(roleName);

  const loadSeq = useRef(0);
  const [tab, setTab] = useState("deals");
  const [orderChip, setOrderChip] = useState("all");
  const [startDate, setStartDate] = useState(() => moment().subtract(30, "days").toDate());
  const [endDate, setEndDate] = useState(() => new Date());
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);

  const showCancelColumn = canCancelPendingOrders && orderChip !== "done";

  const load = useCallback(
    async (page = 1, append = false) => {
      const sess = getSessionData() || {};
      const uid = sess.userId || sess.id || sess.UserId;
      const sessRoles = sess.roles || [sess.roleName || sess.role || "User"];
      const sessIsUser = String(sessRoles[0] || "").toLowerCase() === "user";
      if (!uid) {
        setError("Not logged in");
        return;
      }
      const seq = ++loadSeq.current;
      setLoading(true);
      setError(null);
      try {
        const payload = {
          userId: Number(uid),
          pageNo: page,
          pageSize: 50,
          segmentId: null,
          clientId: sessIsUser ? Number(uid) : null,
          symbol: null,
          searchText: "",
          startDate: formatApiDateYmd(startDate),
          endDate: formatApiDateYmd(endDate),
        };
        const { data: res } = await api.post(endpointForTab(tab), payload);
        if (seq !== loadSeq.current) return;
        if (res?.status !== 200) {
          throw new Error(res?.message || "History API failed");
        }
        const inner = res.data && !Array.isArray(res.data) ? res.data : res;
        const records = inner?.records || inner?.data?.records || (Array.isArray(inner) ? inner : []);
        const total = inner?.totalRecords ?? inner?.data?.totalRecords ?? records.length;
        setTotal(Number(total) || 0);
        setPageNo(page);
        setRows((prev) => (append ? [...prev, ...records] : records));
      } catch (e) {
        if (seq !== loadSeq.current) return;
        setRows([]);
        setTotal(0);
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load. Deploy History SPs and restart API."
        );
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [startDate, endDate, tab]
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  const onCancelPendingOrder = useCallback(
    (orderId) => {
      if (!orderId || !canCancelPendingOrders) return;
      Swal.fire({
        title: "Are you sure?",
        text: "Are you sure you want to cancel this order?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Cancel Order",
        confirmButtonColor: "#885b24",
        cancelButtonColor: "#d33",
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        setCancellingOrderId(orderId);
        try {
          const res = await dispatch(deleteOrder({ id: orderId }));
          if (res?.meta?.requestStatus === "fulfilled") {
            load(1, false);
          }
        } finally {
          setCancellingOrderId(null);
        }
      });
    },
    [canCancelPendingOrders, dispatch, load]
  );

  const visibleRows = useMemo(() => {
    if (tab !== "orders" || orderChip === "all") return rows;
    return rows.filter((r) => {
      const st = Number(r.state ?? 0);
      const completed = String(r.status || "").toLowerCase() === "filled" || st === 2;
      const pending = (st === 0 || st === 1) && !completed;
      return orderChip === "pending" ? pending : !pending;
    });
  }, [rows, tab, orderChip]);

  const renderTable = () => {
    if (loading && !visibleRows.length) {
      return <div className="hist-empty">Loading…</div>;
    }
    if (!loading && visibleRows.length === 0) {
      return (
        <div className="hist-empty">
          {tab === "orders"
            ? "No orders in this date range. Market fills need API restart after Orders insert fix; old deals need backfill."
            : "No records in this date range"}
        </div>
      );
    }

    if (tab === "deals") {
      return (
        <table className="hist-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Symbol</th>
              <th>Entry</th>
              <th className="num">Lots</th>
              <th className="num">Qty</th>
              <th className="num">Price</th>
              <th className="num">Commission</th>
              <th className="num">Profit</th>
              <th>Position#</th>
              <th>Order#</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const dealType = Number(row.dealType);
              const typeName = String(row.dealTypeName || "").toLowerCase();
              const isBuy = dealType === 1 || typeName === "buy";
              const isSell = dealType === 2 || typeName === "sell";
              const typeColor = isBuy ? "#0d9488" : isSell ? "#dc2626" : undefined;
              const profitNum = row.profit == null || row.profit === "" ? null : Number(row.profit);
              const profitColor =
                profitNum == null || !Number.isFinite(profitNum)
                  ? undefined
                  : profitNum > 0
                    ? "#16a34a"
                    : profitNum < 0
                      ? "#dc2626"
                      : undefined;
              return (
                <tr key={row.id}>
                  <td>{fmtTime(row.dealTime, row.createdOn)}</td>
                  <td style={typeColor ? { color: typeColor, fontWeight: 600 } : undefined}>
                    {fmt(row.dealTypeName)}
                  </td>
                  <td title={row.displayName || undefined}>{fmt(row.symbol)}</td>
                  <td>{fmt(row.entryName)}</td>
                  <td className="num">{fmt(row.volumeLots)}</td>
                  <td className="num">{fmt(row.volumeQty)}</td>
                  <td className="num">{fmt(row.price)}</td>
                  <td className="num">{fmt(row.commission)}</td>
                  <td
                    className="num"
                    style={profitColor ? { color: profitColor, fontWeight: 600 } : undefined}
                  >
                    {row.profit == null ? "—" : row.profit}
                  </td>
                  <td>{fmt(row.positionId)}</td>
                  <td>{fmt(row.orderId)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    if (tab === "orders") {
      return (
        <table className="hist-table">
          <thead>
            <tr>
              <th>Setup</th>
              <th>Done</th>
              <th>State</th>
              <th>Buy/Sell</th>
              <th>Type</th>
              <th>Symbol</th>
              <th className="num">Lots</th>
              <th className="num">Qty</th>
              <th className="num">Price</th>
              <th>Order#</th>
              <th>Deal#</th>
              {showCancelColumn && <th>Cancel</th>}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const side = String(row.orderTypeName || "").toLowerCase();
              const sideColor =
                side === "buy" ? "#0d9488" : side === "sell" ? "#dc2626" : undefined;
              const pending = isHistoryOrderPending(row);
              const busy = cancellingOrderId === row.id;
              return (
                <tr key={row.id}>
                  <td>{fmtTime(row.timeSetupText, row.timeSetup)}</td>
                  <td>{fmtTime(row.timeDoneText, row.timeDone)}</td>
                  <td>{fmt(row.stateName || row.status)}</td>
                  <td style={sideColor ? { color: sideColor, fontWeight: 600 } : undefined}>
                    {fmt(row.orderTypeName)}
                  </td>
                  <td>{fmt(row.executionTypeName)}</td>
                  <td title={row.displayName || undefined}>{fmt(row.symbol)}</td>
                  <td className="num">{fmt(row.lot)}</td>
                  <td className="num">{fmt(row.qty)}</td>
                  <td className="num">{fmt(row.rate)}</td>
                  <td>{fmt(row.id)}</td>
                  <td>{fmt(row.firstDealId)}</td>
                  {showCancelColumn && (
                    <td>
                      {pending ? (
                        <button
                          type="button"
                          className="hist-cancel-btn"
                          disabled={busy}
                          onClick={() => onCancelPendingOrder(row.id)}
                        >
                          {busy ? "…" : "Cancel"}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    return (
      <table className="hist-table">
        <thead>
          <tr>
            <th>Open</th>
            <th>Close</th>
            <th>Type</th>
            <th>Symbol</th>
            <th className="num">Lots</th>
            <th className="num">Qty</th>
            <th className="num">Open Price</th>
            <th className="num">Close Price</th>
            <th className="num">Commission</th>
            <th className="num">P&amp;L</th>
            <th>Position#</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => {
            const typeName = String(row.type || "").toLowerCase();
            const isBuy = typeName === "buy";
            const isSell = typeName === "sell";
            const typeColor = isBuy ? "#0d9488" : isSell ? "#dc2626" : undefined;
            const pnlNum =
              row.realizedPnl == null || row.realizedPnl === ""
                ? null
                : Number(row.realizedPnl);
            const pnlColor =
              pnlNum == null || !Number.isFinite(pnlNum)
                ? undefined
                : pnlNum > 0
                  ? "#16a34a"
                  : pnlNum < 0
                    ? "#dc2626"
                    : undefined;
            return (
              <tr key={row.positionId || row.id}>
                <td>{fmtTime(row.openTimeText, row.openTime)}</td>
                <td>{fmtTime(row.closeTimeText, row.closeTime)}</td>
                <td style={typeColor ? { color: typeColor, fontWeight: 600 } : undefined}>
                  {fmt(row.type)}
                </td>
                <td title={row.displayName || undefined}>{fmt(row.symbol)}</td>
                <td className="num">{fmt(row.volumeLots)}</td>
                <td className="num">{fmt(row.volumeQty)}</td>
                <td className="num">{fmt(row.priceOpen)}</td>
                <td className="num">{fmt(row.priceClose)}</td>
                <td className="num">{fmt(row.commission)}</td>
                <td
                  className="num"
                  style={pnlColor ? { color: pnlColor, fontWeight: 600 } : undefined}
                >
                  {fmt(row.realizedPnl)}
                </td>
                <td>{fmt(row.positionId)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="hist-page">
      <div className="hist-page__head">
        <h3 className="hist-page__title">History</h3>
        <span className="hist-page__total">Total: {total}</span>
      </div>

      <div className="hist-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`hist-tabs__btn${tab === t.id ? " is-active" : ""}`}
            onClick={() => {
              setTab(t.id);
              setOrderChip("all");
              setPageNo(1);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="hist-chips">
          {ORDER_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`hist-chips__btn${orderChip === c.id ? " is-active" : ""}`}
              onClick={() => setOrderChip(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="hist-filters">
        <DatePicker selected={startDate} onChange={setStartDate} dateFormat={UI_DATEPICKER_FORMAT} className="form-control" />
        <DatePicker selected={endDate} onChange={setEndDate} dateFormat={UI_DATEPICKER_FORMAT} className="form-control" />
        <button type="button" className="hist-filters__find" onClick={() => load(1, false)}>
          Find
        </button>
      </div>

      {error && <div className="hist-error">{error}</div>}

      <div className="hist-scroll">{renderTable()}</div>

      {!loading && rows.length > 0 && rows.length < total && (
        <button type="button" className="hist-more" onClick={() => load(pageNo + 1, true)}>
          Load more
        </button>
      )}
    </div>
  );
}
